import { useEffect, useState } from "react";
import { UserCircle2, Phone, KeyRound } from "lucide-react";
import { SetPasswordDialog, ImpersonateButton } from "../dashboard/UserAdminActions";
import { Input } from "../ui/input";
import { toast } from "sonner";
import api, { eur, formatApiErrorDetail } from "../../lib/api";
import EditableCell from "../dashboard/EditableCell";

const TYPE = { customer: "Privato", b2b: "B2B" };
const STATUS_OPTS = [{ value: "active", label: "Attivo" }, { value: "suspended", label: "Sospeso" }, { value: "rejected", label: "Rifiutato" }];

export default function MobileUsers() {
  const [users, setUsers] = useState([]);
  const [agents, setAgents] = useState([]);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");
  const [pwdUser, setPwdUser] = useState(null);

  const load = () => Promise.all([api.get("/tables/users"), api.get("/agents")])
    .then(([u, a]) => { setUsers(u.data); setAgents(a.data.filter((x) => (x.status || "active") === "active")); }).catch(() => {});
  useEffect(() => { load(); }, []);

  const inline = (u, field) => async (v) => {
    try { await api.put(`/registrations/${u.id}`, { [field]: v }); toast.success("Salvato"); await load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const cell = (u, field, extra = {}) => <EditableCell value={u[field] ?? (field === "status" ? "active" : field === "discount_pct" ? 0 : "")} onSave={inline(u, field)} testId={`mobile-edit-user-${field}-${u.id}`} {...extra} />;
  const agentOpts = [{ value: "", label: "— Nessuno" }, ...agents.map((a) => ({ value: a.id, label: a.name }))];

  const list = users.filter((u) => (filter === "all" || u.account_type === filter) &&
    (!q || [u.name, u.email, u.business, u.city, u.phone].join(" ").toLowerCase().includes(q.toLowerCase())));

  return (
    <div className="mt-8" data-testid="mobile-users">
      <h2 className="font-serif text-2xl font-semibold text-[#0B2A45] flex items-center gap-2 mb-3"><UserCircle2 className="w-6 h-6 text-[#C2593F]" />Utenti registrati <span className="text-stone-400 text-base">({list.length})</span></h2>
      <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca nome, email, attività, città…" className="h-11 mb-2" data-testid="mobile-users-search" />
      <div className="flex gap-2 mb-4" data-testid="mobile-users-filter">
        {[["all", "Tutti"], ["b2b", "B2B"], ["customer", "Privati"]].map(([k, l]) => (
          <button key={k} onClick={() => setFilter(k)} data-testid={`mobile-users-filter-${k}`}
            className={`h-8 px-3 rounded-full text-xs font-semibold border transition-colors ${filter === k ? "bg-[#0F4C81] text-white border-[#0F4C81]" : "bg-white text-stone-600 border-stone-200"}`}>{l}</button>
        ))}
      </div>

      <div className="space-y-3">
        {list.length === 0 && <p className="text-center text-stone-400 py-10" data-testid="mobile-users-empty">Nessun utente registrato</p>}
        {list.map((u) => (
          <div key={u.id} className="bg-white rounded-2xl border border-stone-200 p-4" data-testid={`mobile-user-${u.id}`}>
            <div className="flex items-start justify-between gap-2 mb-1">
              <span className={`text-[10px] uppercase tracking-wide font-semibold px-2 py-0.5 rounded-full ${u.account_type === "b2b" ? "bg-[#EFF6FF] text-[#0F4C81]" : "bg-stone-100 text-stone-600"}`}>{TYPE[u.account_type] || u.role}</span>
              <div className="w-28">{u.status === "pending" ? <span className="text-[10px] bg-amber-100 text-amber-800 rounded-full px-2 py-0.5">In attesa</span> : cell(u, "status", { options: STATUS_OPTS })}</div>
            </div>
            <div className="font-semibold text-[#0B2A45]">{cell(u, "name")}</div>
            <div className="text-xs text-stone-500">{cell(u, "email", { type: "email" })}</div>
            <div className="text-xs text-stone-500 grid grid-cols-2 gap-1 mt-1">
              {u.account_type === "b2b" && <div className="col-span-2"><span className="text-[10px] uppercase text-stone-400">Attività</span>{cell(u, "business")}</div>}
              <div><span className="text-[10px] uppercase text-stone-400 inline-flex items-center gap-0.5"><Phone className="w-3 h-3" />Tel.</span>{cell(u, "phone", { type: "tel" })}</div>
              <div><span className="text-[10px] uppercase text-stone-400">Città</span>{cell(u, "city")}</div>
              <div className="col-span-2"><span className="text-[10px] uppercase text-stone-400">Indirizzo</span>{cell(u, "address")}</div>
              {u.account_type === "b2b" && (<>
                <div><span className="text-[10px] uppercase text-stone-400">P.IVA</span>{cell(u, "vat")}</div>
                <div><span className="text-[10px] uppercase text-stone-400">Sconto %</span>{cell(u, "discount_pct", { type: "number" })}</div>
                <div className="col-span-2"><span className="text-[10px] uppercase text-stone-400">Agente</span>{cell(u, "agent_id", { options: agentOpts, placeholder: "— Nessuno" })}</div>
              </>)}
            </div>
            <div className="flex justify-between text-[11px] text-stone-400 mt-3 pt-3 border-t border-stone-100">
              <span>Ordini shop: <b className="text-stone-600">{u.orders_count}</b></span>
              <span>Speso: <b className="text-stone-600">{eur(u.spent)}</b></span>
              <span>{(u.created_at || "").slice(0, 10)}</span>
            </div>
            <div className="flex items-center gap-2 mt-3">
              <button onClick={() => setPwdUser(u)} className="flex-1 h-10 rounded-xl bg-stone-50 text-xs font-semibold text-[#0F4C81] flex items-center justify-center gap-1" data-testid={`mobile-set-pwd-${u.id}`}>
                <KeyRound className="w-4 h-4" />{u.admin_set_password ? <span className="font-mono">{u.admin_set_password}</span> : "Imposta password"}
              </button>
              <ImpersonateButton user={u} compact className="flex-1 h-10" />
            </div>
          </div>
        ))}
      </div>
      {pwdUser && <SetPasswordDialog user={pwdUser} onClose={() => setPwdUser(null)} onDone={load} />}
    </div>
  );
}
